import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  VoiceShortcutEditForm,
  VoiceShortcutEditInitialValues,
  VoiceShortcutComponents,
  VoiceShortcutAdditionalParams,
  VoiceShortcutFormOptions,
  onVoiceShortcutFormSaved,
  IVoiceShortcutFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedVoiceShortcut, ISharedPatchVoiceShortcut, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchVoiceShortcut, patchVoiceShortcut, getVoiceShortcutUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedVoiceShortcut> {}

const VoiceShortcutFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchVoiceShortcut(id).then(voice_shortcut => setValue(voice_shortcut));
  }, [id, setValue, ]);

  const param_props: IVoiceShortcutFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && VoiceShortcutEditForm(value, param_props, t), [value, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && VoiceShortcutFormOptions(value, param_props, t), [value, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchVoiceShortcut, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchVoiceShortcut) => value && save(() => patchVoiceShortcut(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onVoiceShortcutFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(value.organization_id, CrudEnum.Update, PermissionEnum.VoiceShortcut, value)}
    >
      <AtlasForm<ISharedPatchVoiceShortcut>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={VoiceShortcutComponents}
        additionalParams={VoiceShortcutAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={VoiceShortcutEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('VOICE_SHORTCUT')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const VoiceShortcutFormEditView = React.memo(withEdit(VoiceShortcutFormEdit, getVoiceShortcutUpdateSchema));
